import React from "react";
import { FormControl } from "../Controls/FormControl";
import { IFormDataAccessor } from "../Utilities/IFormDataAccessor";
import { BorderStyle, FormDesignConstants } from "../Utilities/FormDesignConstants";
import { PropertyEditorInteger } from "../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorSelect } from "../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorColor } from "../../CommonUI/PropertyEditing/PropertyEditorColor";
import { ValidationIssue } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { DefaultOnOff } from "../Utilities/DefaultOnOff";
import { PropertyEditorToggleButtons } from "../../CommonUI/PropertyEditing/PropertyEditorToggleButtons";
import { ControlDiv, EmptyContainer } from "../Builder/EmptyContainer";
import { FormContext } from "../Utilities/FormContext";
import { MovingIcon } from "../../CommonUI/Icon/MovingIcon";
import { ChildFullWidthDiv } from "../Controls/Web/CommonStyled";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { PropertyEditorCollapse } from "../../CommonUI/PropertyEditing/PropertyEditorCollapse";

export abstract class XBaseContainer extends FormControl implements IFormDataAccessor {
    overrideFormBorderSettings: DefaultOnOff = DefaultOnOff.DEFAULT;
    showContainerBorder?: boolean;
    containerBorderType?: string;
    containerBorderRadius?: number;
    containerBorderWidth?: number;
    containerBorderStyle?: BorderStyle;

    containerBorderColor?: string;
    containerBackgroundColor?: string;

    // to implement
    abstract remove(control: FormControl): void;

    abstract find(id: string): FormControl | null;

    abstract setOrder(id: string, order: number): void;

    abstract add(control: FormControl, index?: number): void;

    abstract getControls(): FormControl[];

    getStorageData(): object {
        let d = super.getStorageData();

        return DataUtilities.addStorageData(
            this,
            d,
            "overrideFormBorderSettings",
            "showContainerBorder",
            "containerBorderType",
            "containerBorderRadius",
            "containerBorderWidth",
            "containerBorderStyle",
            "containerBorderColor",
            "containerBackgroundColor",
        );
    }

    setStorageData(data: object): void {
        super.setStorageData(data);
        DataUtilities.setStorageData(
            this,
            data,
            "overrideFormBorderSettings",
            "showContainerBorder",
            "containerBorderType",
            "containerBorderRadius",
            "containerBorderWidth",
            "containerBorderStyle",
            "containerBorderColor",
            "containerBackgroundColor",
        );
    }

    getDesignValidationIssues(): ValidationIssue[] {
        return [];
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return;
    }

    renderEmptyControlDiv(colGap?: number, message?: string) {
        return (
            <EmptyContainer colGap={colGap}>
                <span>{`${message || "Drag controls from toolbox."}`}</span>
                <ControlDiv>
                    Control <MovingIcon />
                </ControlDiv>
            </EmptyContainer>
        );
    }

    renderBorderConfigUI(): JSX.Element | null {
        return (
            <ChildFullWidthDiv>
                <PropertyEditorToggleButtons
                    editObject={this}
                    label="Override Border Settings"
                    propertyName="overrideFormBorderSettings"
                />

                <ObserveState
                    listenTo={this}
                    control={() => {
                        return (
                            <PropertyEditorCollapse
                                editObject={this}
                                collapseLogicOrField={() => this.overrideFormBorderSettings === DefaultOnOff.On}
                                observeProperties={["overrideFormBorderSettings"]}
                            >
                                <ChildFullWidthDiv>
                                    <PropertyEditorInteger
                                        editObject={this}
                                        label="Border Width"
                                        propertyName="containerBorderWidth"
                                        parentDefaultValue={this.getFormContext()?.form.defaultContainerBorderWidth}
                                    />
                                    <PropertyEditorInteger
                                        editObject={this}
                                        label="Border Radius"
                                        propertyName="containerBorderRadius"
                                        parentDefaultValue={this.getFormContext()?.form?.defaultContainerBorderRadius}
                                    />

                                    <PropertyEditorColor
                                        editObject={this}
                                        label="Border Color"
                                        propertyName="containerBorderColor"
                                        parentDefaultValue={this.getFormContext()?.form?.defaultContainerBorderColor}
                                    />
                                    <PropertyEditorSelect
                                        editObject={this}
                                        label="Border Style"
                                        propertyName="containerBorderStyle"
                                        options={FormDesignConstants.BORDER_STYLES}
                                        parentDefaultValue={this.getFormContext()?.form?.defaultContainerBorderStyle}
                                    />
                                </ChildFullWidthDiv>
                            </PropertyEditorCollapse>
                        );
                    }}
                />
            </ChildFullWidthDiv>
        );
    }

    hasOwnBorder(): boolean | undefined {
        return this.overrideFormBorderSettings === DefaultOnOff.On;
    }

    hasBorder(): boolean | undefined {
        return (
            this.hasOwnBorder() ||
            (this.overrideFormBorderSettings === DefaultOnOff.DEFAULT &&
                this.getFormContext()?.form?.defaultShowContainerBorder)
        );
    }

    borderColor(): string | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderColor) ||
            this.getFormContext()?.form?.defaultContainerBorderColor
        );
    }

    borderStyle(): BorderStyle | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderStyle) ||
            this.getFormContext()?.form?.defaultContainerBorderStyle
        );
    }

    borderWidth(): number | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderWidth) ||
            this.getFormContext()?.form?.defaultContainerBorderWidth
        );
    }

    borderRadius(): number | undefined {
        return (
            (this.hasOwnBorder() && this.containerBorderRadius) ||
            this.getFormContext()?.form?.defaultContainerBorderRadius
        );
    }

    backgroundColor(): string | undefined | null {
        return this.containerBackgroundColor || this.getFormContext()?.form?.defaultContainerBackgroundColor;
    }

    // ----- Overrides to set contexts ----

    setFormContext(value: FormContext | undefined): void {
        if (!value) return;

        super.setFormContext(value);

        let controls = this.getControls();
        if (controls) {
            for (let control of controls) {
                // set edit context first
                control.setFormContext(value);
            }
        }
    }
}
